import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { getLead, getLeads, leadErrorMessage } from './leadsApi';
import './LeadsView.css';

const emptyFilters = { q: '', service: '', from: '', to: '' };
const formatDate = (value) => value && Number.isFinite(new Date(value).getTime())
    ? new Intl.DateTimeFormat('es-PA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Panama' }).format(new Date(value)) : '—';
const services = (value) => Array.isArray(value) ? value.join(', ') || '—' : '—';
export const safeSourceUrl = (value) => {
    try {
        const url = new URL(value);
        return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch (_) { return null; }
};

export default function LeadsView() {
    const [draft, setDraft] = useState(emptyFilters);
    const [filters, setFilters] = useState({ ...emptyFilters, page: 1, pageSize: 25 });
    const [revision, setRevision] = useState(0);
    const [list, setList] = useState({ loading: true });
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState({ loading: true });
    const [detailRevision, setDetailRevision] = useState(0);
    useEffect(() => {
        let active = true;
        setList({ loading: true });
        getLeads(filters).then((data) => { if (active) setList({ data }); })
            .catch((error) => { if (active) setList({ error: leadErrorMessage(error) }); });
        return () => { active = false; };
    }, [filters, revision]);
    useEffect(() => {
        if (!selected) return undefined;
        let active = true;
        setDetail({ loading: true });
        getLead(selected).then((data) => { if (active) setDetail({ data }); })
            .catch((error) => { if (active) setDetail({ error: leadErrorMessage(error) }); });
        return () => { active = false; };
    }, [selected, detailRevision]);
    const lead = detail.data;
    const sourceUrl = safeSourceUrl(lead?.sourceUrl);
    const hasFilters = Object.keys(emptyFilters).some((key) => filters[key]);
    const update = (event) => setDraft({ ...draft, [event.target.name]: event.target.value });
    return <section className="sales-leads" aria-label="Leads comerciales">
        <div className="sales-leads-heading">
            <div><h2>Leads recibidos</h2><p>Solicitudes del formulario web de Coffee Vending. Fechas en hora de Panamá.</p></div>
            <button type="button" onClick={() => setRevision((value) => value + 1)} disabled={list.loading}>Actualizar</button>
        </div>
        <form className="sales-leads-filters" onSubmit={(event) => { event.preventDefault(); setFilters({ ...draft, page: 1, pageSize: 25 }); }}>
            <label>Buscar<input name="q" value={draft.q} onChange={update} maxLength={100} placeholder="Nombre, empresa, correo o teléfono" /></label>
            <label>Servicio<input name="service" value={draft.service} onChange={update} maxLength={100} placeholder="Servicio solicitado" /></label>
            <label>Desde<input type="date" name="from" value={draft.from} onChange={update} max={draft.to || undefined} /></label>
            <label>Hasta<input type="date" name="to" value={draft.to} onChange={update} min={draft.from || undefined} /></label>
            <button type="submit">Aplicar filtros</button>
            <button type="button" onClick={() => { setDraft(emptyFilters); setFilters({ ...emptyFilters, page: 1, pageSize: 25 }); }}>Limpiar</button>
        </form>
        <div aria-live="polite" aria-busy={Boolean(list.loading)}>
            {list.loading && <p role="status">Cargando leads…</p>}
            {list.error && <div role="alert"><p>{list.error}</p><button type="button" onClick={() => setRevision((value) => value + 1)}>Reintentar</button></div>}
            {list.data && <>
                <p>{list.data.total} solicitudes encontradas</p>
                {!list.data.items.length ? <p>{hasFilters ? 'No hay leads que coincidan con estos filtros.' : 'Todavía no hay leads en esta página.'}</p> :
                    <div className="sales-leads-table-wrap"><table>
                        <caption className="sales-leads-sr-only">Solicitudes recibidas, más recientes primero</caption>
                        <thead><tr>{['Recibido', 'Empresa / contacto', 'Correo / teléfono', 'Colaboradores', 'Servicios', 'Detalle'].map((title) => <th key={title} scope="col">{title}</th>)}</tr></thead>
                        <tbody>{list.data.items.map((item) => <tr key={item._id}>
                            <td>{formatDate(item.createdAt)}</td><td><strong>{item.empresa || '—'}</strong><br />{item.nombre || '—'}</td>
                            <td>{item.email || '—'}<br />{item.telefono || '—'}</td><td>{item.colaboradores ?? '—'}</td><td>{services(item.servicio)}</td>
                            <td><button type="button" aria-label={`Ver detalle de ${item.nombre || item.empresa || 'lead'}`} onClick={() => { setDetail({ loading: true }); setSelected(item._id); }}>Ver detalle</button></td>
                        </tr>)}</tbody>
                    </table></div>}
                <nav className="sales-leads-pagination" aria-label="Paginación de leads">
                    <button type="button" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Anterior</button>
                    <span>Página {filters.page} de {Math.max(1, list.data.totalPages)}</span>
                    <button type="button" disabled={filters.page >= list.data.totalPages || filters.page >= 1000} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Siguiente</button>
                </nav>
            </>}
        </div>
        <Dialog header="Detalle del lead" visible={Boolean(selected)} onHide={() => setSelected(null)} style={{ width: '42rem', maxWidth: '95vw' }} modal draggable={false}>
            {detail.loading && <p role="status">Cargando detalle…</p>}
            {detail.error && <div role="alert"><p>{detail.error}</p><button type="button" onClick={() => setDetailRevision((value) => value + 1)}>Reintentar detalle</button></div>}
            {lead && <dl className="sales-leads-detail">
                {[['Empresa', lead.empresa], ['Contacto', lead.nombre], ['Correo', lead.email], ['Teléfono', lead.telefono], ['Colaboradores', lead.colaboradores], ['Servicios', services(lead.servicio)], ['Recibido', formatDate(lead.createdAt)], ['Origen', lead.source], ['Mensaje', lead.mensaje]].map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{value ?? '—'}</dd></React.Fragment>)}
                <dt>Página de origen</dt><dd>{sourceUrl ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer">{lead.sourceUrl}</a> : lead.sourceUrl || '—'}</dd>
            </dl>}
        </Dialog>
    </section>;
}
